/**
 * PasskeyPrompt
 *
 * Runs once per workspace session: if the user is signed-in on a device
 * that supports platform biometrics and has no passkeys registered yet,
 * fires a Sonner toast offering to enrol Face ID / Touch ID / fingerprint.
 *
 * Dismissals are remembered in localStorage for 14 days so we don't nag.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  isPasskeySupported,
  isPlatformAuthenticatorAvailable,
  listCredentials,
  usePasskey,
} from "@/lib/webauthn";
import { useAuth } from "@/lib/auth";

const DISMISS_KEY_PREFIX = "sres_passkey_prompt_dismissed_";
const DISMISS_DAYS = 14;

export default function PasskeyPrompt() {
  const { user } = useAuth();
  const fired = useRef(false);
  const { registerDevice } = usePasskey();

  useEffect(() => {
    if (fired.current) return;
    if (!user?.id) return;

    (async () => {
      try {
        if (!isPasskeySupported()) return;
        const platform = await isPlatformAuthenticatorAvailable();
        if (!platform) return;

        // Respect a recent dismissal.
        const dismissKey = `${DISMISS_KEY_PREFIX}${user.id}`;
        const dismissedAt = Number(localStorage.getItem(dismissKey) || 0);
        if (Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;

        // Skip if the user already has at least one passkey.
        const creds = await listCredentials().catch(() => []);
        if (creds.length > 0) return;

        fired.current = true;

        const enrol = async () => {
          try {
            const label = deviceLabel();
            await registerDevice(label);
            toast.success(`Passkey saved — you can now sign in with biometrics on ${label}`);
          } catch (e) {
            if (!/cancel/i.test(String(e?.message || ""))) {
              toast.error(e?.message || "Passkey registration failed");
            }
          }
        };

        const dismiss = () => {
          localStorage.setItem(dismissKey, String(Date.now()));
        };

        toast("Enable biometric sign-in?", {
          description:
            "Use Face ID / Touch ID / your device PIN to sign in to SRES next time — no password needed.",
          duration: 20_000,
          onDismiss: dismiss,
          onAutoClose: dismiss,
          action: { label: "Enable", onClick: enrol },
          cancel: { label: "Not now", onClick: dismiss },
        });
      } catch {
        /* silent — this prompt is purely additive */
      }
    })();
  }, [user?.id, registerDevice]);

  return null;
}

/**
 * Best-effort human-readable label for the current device — used as the
 * default `device_label` when a passkey is enrolled from this session.
 */
function deviceLabel() {
  const ua = navigator.userAgent || "";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? "Android phone" : "Android tablet";
  if (/Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  if (/Linux/.test(ua)) return "Linux";
  return "This device";
}
